/**
 * A venue name as a comparable key (Phase 33 D-06, spec-desk-venue-location
 * D-01 / D-02).
 *
 * This is the one place the stopword logic lives (D-02): the desk's listed
 * venue lookup (`matchVenueByName` in deskPrefill) and the custom-venue
 * history merge (`venueHistory`) both compare names through it. It sits in its
 * own module so venueHistory can import it without a cycle through deskPrefill.
 *
 * Phase 33.4 (D-01, D-03, D-08): this file is the desk's copy of the editor's
 * `mobile/supabase/functions/_shared/editor/venueKey.ts` (its Deno twin; admin and
 * mobile share no dependency graph). It carries the same key, compact key (D-04)
 * and layer-A matcher (D-05..D-07), line for line, with the admin's row shapes
 * (`city_id`, `venue_id`). Two tests keep the copies equal:
 * `venueKey.parity.test.ts` pins the `VENUE_STOPWORDS` literal against the Deno
 * source, and `venueKey.fixtures.test.ts` runs the shared
 * `venueKey.fixtures.json` that the editor's `venueKey_test.ts` also runs.
 *
 * Pure: no Supabase, no React, no i18n.
 */

// Linking words a venue name may or may not carry ("Centro de Artes" vs
// "Centro Artes"): ignored when comparing names. Pinned against the editor's
// literal by venueKey.parity.test.ts (D-02: same words, same order).
export const VENUE_STOPWORDS = new Set(['a', 'as', 'o', 'os', 'e', 'de', 'do', 'da', 'dos', 'das', 'the', 'of'])

/** A venue name as a comparable key: no accents, case, punctuation or linking words. */
export const venueNameKey = (name: string | null | undefined): string =>
  (name ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length > 0 && !VENUE_STOPWORDS.has(word))
    .join(' ')

/** The key with its spaces removed (D-04): 'cine teatro sousa telles' -> 'cineteatrosousatelles'. */
export const compactVenueKey = (name: string | null | undefined): string => venueNameKey(name).replace(/ /g, '')

// P-10: a dash only with whitespace on both sides (so 'Cine-Teatro' stays one
// word), a comma, a slash or a pipe. Parenthesised groups are pulled out first.
const PART_SEPARATOR = /\s[-–—]\s|[,/|]/
const PAREN_GROUP = /\(([^()]*)\)/g

/**
 * A venue text split into the parts D-05 step 2 looks up: on ' - ', ' – ', ' — ',
 * ',', '/', '|', and each '(...)' group as its own part (after the text around it).
 * Parts are trimmed; a part whose key is empty is dropped. A text with no
 * separator gives one part.
 */
export const splitVenueParts = (text: string): string[] => {
  const groups: string[] = []
  const outside = text.replace(PAREN_GROUP, (_whole: string, inner: string) => {
    groups.push(inner)
    return '|'
  })
  return [outside, ...groups]
    .flatMap((piece) => piece.split(PART_SEPARATOR))
    .map((part) => part.trim())
    .filter((part) => venueNameKey(part) !== '')
}

/** A listed venue, as the desk loads it. */
export type VenueKeyRow = { id: number; name: string; city_id: number | null }

/** A venue alias (migration 0039 `venue_aliases`): another name for one listed venue. */
export type VenueAliasRow = { alias: string; venue_id: number }

/** One index entry: the venue a key points at, and whether it came from an alias. */
export type VenueHit = { venueId: number; viaAlias: boolean }

/** Venue names and aliases by `${cityId}|${key}` and by `${cityId}|${compact}`. */
export type VenueIndex = {
  byKey: Map<string, VenueHit[]>
  byCompact: Map<string, VenueHit[]>
}

const addHit = (map: Map<string, VenueHit[]>, mapKey: string, hit: VenueHit): void => {
  const hits = map.get(mapKey)
  if (hits === undefined) {
    map.set(mapKey, [hit])
  } else if (!hits.some((known) => known.venueId === hit.venueId && known.viaAlias === hit.viaAlias)) {
    hits.push(hit)
  }
}

const addName = (index: VenueIndex, cityId: number, name: string, hit: VenueHit): void => {
  const key = venueNameKey(name)
  if (key.length === 0) return
  addHit(index.byKey, `${cityId}|${key}`, hit)
  addHit(index.byCompact, `${cityId}|${key.replace(/ /g, '')}`, hit)
}

/**
 * The town-scoped index of venue names and aliases. A venue with no town or an
 * empty key is skipped; an alias inherits its venue's town, and an alias whose
 * venue is not in `venues` is skipped. Names are indexed whole, never split (D-06).
 */
export const buildVenueIndex = (venues: readonly VenueKeyRow[], aliases: readonly VenueAliasRow[]): VenueIndex => {
  const index: VenueIndex = { byKey: new Map(), byCompact: new Map() }
  const cityOf = new Map<number, number>()
  for (const venue of venues) {
    if (venue.city_id === null) continue
    cityOf.set(venue.id, venue.city_id)
    addName(index, venue.city_id, venue.name, { venueId: venue.id, viaAlias: false })
  }
  for (const alias of aliases) {
    const cityId = cityOf.get(alias.venue_id)
    if (cityId === undefined) continue
    addName(index, cityId, alias.alias, { venueId: alias.venue_id, viaAlias: true })
  }
  return index
}

/**
 * D-05 step 1 for one string: the venues of the town whose name or alias has the
 * text's key or compact key. `ids` is sorted ascending and de-duplicated;
 * `viaAliasOnly` is true when every hit came from an alias (no name decided it).
 */
export const venueIdsForText = (
  text: string | null | undefined,
  cityId: number | null,
  index: VenueIndex
): { ids: number[]; viaAliasOnly: boolean } => {
  const key = venueNameKey(text)
  if (cityId === null || key.length === 0) return { ids: [], viaAliasOnly: false }
  const hits = [
    ...(index.byKey.get(`${cityId}|${key}`) ?? []),
    ...(index.byCompact.get(`${cityId}|${key.replace(/ /g, '')}`) ?? [])
  ]
  const ids = Array.from(new Set(hits.map((hit) => hit.venueId))).sort((a, b) => a - b)
  return { ids, viaAliasOnly: hits.length > 0 && hits.every((hit) => hit.viaAlias) }
}

/** How layer A found the venue: by a name (whole, compact or a part's) or by an alias. */
export type VenueMatch = { venueId: number; how: 'exact' | 'alias' }

/**
 * Layer A (D-05): the one listed venue of the town the venue text names, or null.
 * No town or an empty key -> null. Two or more venues at any step -> null.
 */
export const matchVenueLayerA = (
  text: string | null | undefined,
  cityId: number | null,
  index: VenueIndex
): VenueMatch | null => {
  if (cityId === null || venueNameKey(text) === '') return null
  const whole = venueIdsForText(text, cityId, index)
  if (whole.ids.length === 1) return { venueId: whole.ids[0], how: whole.viaAliasOnly ? 'alias' : 'exact' }
  if (whole.ids.length > 1) return null

  const parts = splitVenueParts(text ?? '')
  if (parts.length < 2) return null
  const found = new Set<number>()
  let viaAlias = false
  for (const part of parts) {
    const hit = venueIdsForText(part, cityId, index)
    if (hit.ids.length > 1) return null
    if (hit.ids.length === 1) {
      found.add(hit.ids[0])
      if (hit.viaAliasOnly) viaAlias = true
    }
  }
  if (found.size !== 1) return null
  return { venueId: Array.from(found)[0], how: viaAlias ? 'alias' : 'exact' }
}

/**
 * Every venue of the town that shares a key or compact key (aliases included)
 * with another venue of the town: the duplicates layer A never links (D-05.3).
 */
export const duplicateVenueIds = (cityId: number, index: VenueIndex): Set<number> => {
  const prefix = `${cityId}|`
  const duplicates = new Set<number>()
  for (const map of [index.byKey, index.byCompact]) {
    map.forEach((hits, mapKey) => {
      if (!mapKey.startsWith(prefix)) return
      const ids = new Set(hits.map((hit) => hit.venueId))
      if (ids.size > 1) ids.forEach((id) => duplicates.add(id))
    })
  }
  return duplicates
}
